import type {
  BoardListItem,
  BoardDetail,
  BoardTemplate,
  BoardMemberRole,
  BoardRoleMatrix,
  ProjectMemberItem,
} from '@hygge/shared';
import { api } from './api';

export async function getBoards(params?: {
  projectId?: string;
  search?: string;
}): Promise<BoardListItem[]> {
  const { data } = await api.get<BoardListItem[]>('/boards', { params });
  return data;
}

export async function getBoard(id: string): Promise<BoardDetail> {
  const { data } = await api.get<BoardDetail>(`/boards/${id}`);
  return data;
}

export async function createBoard(payload: {
  name: string;
  slug: string;
  description?: string;
  color?: string;
  icon?: string;
  projectId?: string;
  /** Members of `projectId` to add as board Members — linking alone admits nobody (HW-983). */
  projectMemberIds?: string[];
  departmentId?: string;
  template?: BoardTemplate;
  addDepartmentMembers?: boolean;
  visibility?: 'public' | 'members_only';
}): Promise<BoardDetail> {
  const { data } = await api.post<BoardDetail>('/boards', payload);
  return data;
}

export async function updateBoard(
  id: string,
  payload: {
    name?: string;
    summary?: string;
    description?: string;
    color?: string;
    icon?: string;
    visibility?: string;
    status?: string;
    priority?: string;
    targetDate?: string | null;
    leadId?: string | null;
    projectId?: string | null;
    /** Members of the board's project to add as board Members; needs `member.manage` (HW-983). */
    projectMemberIds?: string[];
  },
): Promise<BoardDetail> {
  const { data } = await api.patch<BoardDetail>(`/boards/${id}`, payload);
  return data;
}

export async function deleteBoard(id: string): Promise<void> {
  await api.delete(`/boards/${id}`);
}

export async function suggestBoardSlug(name: string): Promise<{ slug: string }> {
  const { data } = await api.get<{ slug: string }>('/boards/slug-suggest', {
    params: { name },
  });
  return data;
}

// ─── Members ───────────────────────────────────────────────────────────

/**
 * A board's member list. `role` is the board row's own role, null for somebody
 * without one; `effectiveRole` is what the board gives them either way, and
 * `source` says where that comes from — their row, or being the board's
 * creator. A board's project admits nobody, so the server no longer sends
 * `'project'`, and `viaProject` is always false; both stay in the type for
 * older readers.
 */
export type BoardRosterEntry = ProjectMemberItem & {
  effectiveRole: BoardMemberRole | null;
  source: 'board' | 'project' | 'creator';
  viaProject: boolean;
};

export async function getBoardMembers(boardId: string): Promise<BoardRosterEntry[]> {
  const { data } = await api.get<BoardRosterEntry[]>(`/boards/${boardId}/members`);
  return data;
}

export async function addBoardMembers(
  boardId: string,
  employeeIds: string[],
  role?: BoardMemberRole,
): Promise<BoardRosterEntry[]> {
  const { data } = await api.post<BoardRosterEntry[]>(`/boards/${boardId}/members`, { employeeIds, ...(role && { role }) });
  return data;
}

export async function changeBoardMemberRole(
  boardId: string,
  employeeId: string,
  role: string,
): Promise<ProjectMemberItem[]> {
  const { data } = await api.patch<ProjectMemberItem[]>(`/boards/${boardId}/members/${employeeId}/role`, { role });
  return data;
}

export async function removeBoardMember(
  boardId: string,
  employeeId: string,
): Promise<void> {
  await api.delete(`/boards/${boardId}/members/${employeeId}`);
}

/** A board's sub-matrix, always full; `customized` is false while it follows the presets. */
export interface BoardRoleMatrixResponse {
  matrix: BoardRoleMatrix;
  customized: boolean;
}

export async function getBoardRoleMatrix(boardId: string): Promise<BoardRoleMatrixResponse> {
  const { data } = await api.get<BoardRoleMatrixResponse>(`/boards/${boardId}/role-matrix`);
  return data;
}

/**
 * Replaces the board's table. A role left out keeps whatever the board already
 * gives it, so the editor may send one row at a time.
 */
export async function setBoardRoleMatrix(
  boardId: string,
  payload: Partial<BoardRoleMatrix>,
): Promise<BoardRoleMatrixResponse> {
  const { data } = await api.put<BoardRoleMatrixResponse>(`/boards/${boardId}/role-matrix`, payload);
  return data;
}

export async function resetBoardRoleMatrix(boardId: string): Promise<BoardRoleMatrixResponse> {
  const { data } = await api.delete<BoardRoleMatrixResponse>(`/boards/${boardId}/role-matrix`);
  return data;
}
